import { AxiosResponse } from "axios";
import {
  contains,
  decoratorIsNotNullable,
  dynamicNamespace,
  equals,
  mergeFilters,
  select,
} from "utils/dynamic";
import {
  API,
  apiRtk,
  getByIDRequestHelper,
  makePatchArgs,
  rtkAdapterDynamicToSource,
  RTK_TAGS,
} from "utils/service";
import { PatchPartial } from "utils/types";
import { ISensitivity } from "__generated__/api";
import {
  IGridSensitivityParams,
  IGridSensitivityResponse,
  ISourceSensitivityParams,
} from "./models";

const dynamic = dynamicNamespace<ISensitivity>();
const REVALIDATE_KEY = RTK_TAGS.SENSITIVITIES;

export const apiSensitivities = apiRtk.injectEndpoints({
  endpoints: (build) => ({
    getSourceSensitivities: build.query({
      queryFn: async ({
        useForPharmAssist,
        useForVitAssist,
      }: Partial<ISourceSensitivityParams>) => {
        try {
          const res = await API.api.sensitivitiesGetAllDynamicCreate({
            select: select("id", "catalogName as title"),
            filter: mergeFilters(
              dynamic.makeFilter("isActive", true, equals),
              dynamic.makeFilter(
                "useForPharmAssist",
                useForPharmAssist,
                decoratorIsNotNullable(equals),
              ),
              dynamic.makeFilter(
                "useForVitAssist",
                useForVitAssist,
                decoratorIsNotNullable(equals),
              ),
            ).join("&&"),
            orderBy: "catalogName.trim()",
          });

          return rtkAdapterDynamicToSource(res) as AxiosResponse<
            Array<{ id: string; title: string }>
          >;
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: RTK_TAGS.SENSITIVITIES }],
    }),
    getGridSensitivities: build.query<
      IGridSensitivityResponse,
      IGridSensitivityParams
    >({
      queryFn: async ({
        search,
        useForPharmAssist,
        take: Take,
        skip: Skip,
        orderBy,
        isActive,
      }) => {
        try {
          const result = await API.api.sensitivitiesGetAllDynamicCreate({
            select: dynamic.select("id", "catalogName", "isActive"),
            filter: mergeFilters(
              dynamic.makeFilter(["catalogName"], search, contains),
              dynamic.makeFilter(
                "useForPharmAssist",
                useForPharmAssist ? true : null,
                decoratorIsNotNullable(equals),
              ),
              dynamic.makeFilter(
                "useForVitAssist",
                useForPharmAssist ? null : true,
                decoratorIsNotNullable(equals),
              ),
              dynamic.makeFilter(
                "isActive",
                isActive,
                decoratorIsNotNullable(equals),
              ),
            ).join("&&"),
            orderBy: dynamic.orderBy(orderBy.field, orderBy.order),
            take: Take,
            skip: Skip,
            count: true,
          });
          return result as AxiosResponse<IGridSensitivityResponse>;
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: REVALIDATE_KEY }],
    }),
    getAllSensitivities: build.query<ISensitivity[], void>({
      queryFn: async () => {
        try {
          const response = await API.api.sensitivitiesGetAllDynamicCreate({
            select: dynamic.select("id", "catalogName as title"),
            filter: mergeFilters(
              dynamic.makeFilter("useForVitAssist", true, equals),
            ).join("&&"),
            orderBy: dynamic.orderBy("catalogName", "asc"),
          });
          return rtkAdapterDynamicToSource<ISensitivity>(
            response as unknown as any,
          );
        } catch (error: any) {
          return { error };
        }
      },
    }),
    getSensitivity: build.query({
      queryFn: async (id: string) => {
        try {
          const result = await getByIDRequestHelper({
            apiFunc: API.api.sensitivitiesGetAllDynamicCreate,
            value: id,
          });
          return result;
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (_, __, id) => [{ type: REVALIDATE_KEY, id }],
    }),
    postSensitivity: build.mutation<ISensitivity, ISensitivity>({
      queryFn: async (data) => {
        try {
          const result = await API.api.sensitivitiesCreateCreate(data);
          return { data: result.data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: REVALIDATE_KEY }],
    }),
    patchSensitivity: build.mutation<void, PatchPartial<ISensitivity, "id">>({
      queryFn: async (data) => {
        try {
          await API.api.sensitivitiesPatchPartialUpdate(...makePatchArgs(data));
          return { data: undefined };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (_, __, data) => [
        { type: REVALIDATE_KEY },
        { type: REVALIDATE_KEY, id: String(data?.id) },
      ],
    }),
    deleteSensitivity: build.mutation<void, { id: string }>({
      queryFn: async (data) => {
        try {
          await API.api.sensitivitiesDeleteDelete(String(data?.id));
          return { data: undefined };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: REVALIDATE_KEY }],
    }),
  }),
});
